if(!customElements.get('dual-media-slideshow')) {
  class DualMediaSlideshow extends HTMLDivElement {
    constructor() {
      super();

      this.swiperLeft = this.querySelector('[data-swiper-left]');
      this.swiperRight = this.querySelector('[data-swiper-right]');
      this.swiperMobile = this.querySelector('[data-swiper-mobile]');

      this.zoomEnabled = this.dataset.mobileZoom === 'true';
      this.zoomModal = null;
      this.zoomStage = null;
      this.zoomImage = null;

      this.zoomScale = 1;
      this.zoomX = 0;
      this.zoomY = 0;
      this.zoomPointers = new Map();
      this.pointerStarts = new Map();
      this.pinchStartDistance = 0;
      this.pinchStartScale = 1;
      this.panStart = null;

      this.lastSourceTap = 0;
      this.lastViewerTap = 0;
      this.sourceTapStart = null;

      this.onSourcePointerDown = this.onSourcePointerDown.bind(this);
      this.onSourcePointerUp = this.onSourcePointerUp.bind(this);
      this.onViewerPointerDown = this.onViewerPointerDown.bind(this);
      this.onViewerPointerMove = this.onViewerPointerMove.bind(this);
      this.onViewerPointerUp = this.onViewerPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      if(Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const target = event.target;
          if(target.classList.contains('dual-media-slide')) {
            const index = target.dataset.swiperSlideIndex;
            if(this.swiperLeft?.swiper) this.swiperLeft.swiper.slideTo(index);
            if(this.swiperRight?.swiper) this.swiperRight.swiper.slideTo(index);
            if(this.swiperMobile?.swiper) this.swiperMobile.swiper.slideTo(index);
          }
        });
      }
    }

    connectedCallback() {
      if(this.swiperLeft) {
        this.swiperLeft.swiper = new Swiper(
          this.swiperLeft,
          JSON.parse(this.swiperLeft.getAttribute('data-swiper-left'))
        );
      }

      if(this.swiperRight) {
        this.swiperRight.swiper = new Swiper(
          this.swiperRight,
          JSON.parse(this.swiperRight.getAttribute('data-swiper-right'))
        );
      }

      if(this.swiperMobile) {
        this.swiperMobile.swiper = new Swiper(
          this.swiperMobile,
          JSON.parse(this.swiperMobile.getAttribute('data-swiper-mobile'))
        );
      }

      this.setupSynchronizedControls();

      if(this.zoomEnabled && window.matchMedia('(max-width: 767px)').matches) {
        this.setupMobileZoom();
      }
    }

    setupSynchronizedControls() {
      if (!this.swiperLeft?.swiper || !this.swiperRight?.swiper) return;

      this.swiperLeft.swiper.on('slideChange', () => {
        setTimeout(() => {
          if(this.swiperRight?.swiper) {
            this.swiperRight.swiper.slideTo(this.swiperLeft.swiper.activeIndex);
          }
        }, 300);
      });

      this.swiperRight.swiper.on('slideChange', () => {
        setTimeout(() => {
          if(this.swiperLeft?.swiper) {
            this.swiperLeft.swiper.slideTo(this.swiperRight.swiper.activeIndex);
          }
        }, 300);
      });
    }

    setupMobileZoom() {
      const mobile = this.querySelector('.dual-media-slideshow--mobile');
      if(!mobile) return;

      mobile.addEventListener('pointerdown', this.onSourcePointerDown, { passive: true });
      mobile.addEventListener('pointerup', this.onSourcePointerUp, { passive: false });

      this.createZoomModal();
    }

    createZoomModal() {
      if(this.zoomModal) return;

      const modal = document.createElement('div');
      modal.className = 'rv-mobile-image-zoom';
      modal.setAttribute('aria-hidden', 'true');
      modal.innerHTML = `
        <button type="button" class="rv-mobile-image-zoom__close" aria-label="Close image viewer">×</button>
        <div class="rv-mobile-image-zoom__stage">
          <img class="rv-mobile-image-zoom__image" alt="">
        </div>
        <div class="rv-mobile-image-zoom__hint">Pinch or double-tap to zoom</div>
      `;

      document.body.appendChild(modal);

      this.zoomModal = modal;
      this.zoomStage = modal.querySelector('.rv-mobile-image-zoom__stage');
      this.zoomImage = modal.querySelector('.rv-mobile-image-zoom__image');

      modal.querySelector('.rv-mobile-image-zoom__close')?.addEventListener('click', () => this.closeZoom());

      this.zoomStage.addEventListener('pointerdown', this.onViewerPointerDown);
      this.zoomStage.addEventListener('pointermove', this.onViewerPointerMove);
      this.zoomStage.addEventListener('pointerup', this.onViewerPointerUp);
      this.zoomStage.addEventListener('pointercancel', this.onViewerPointerUp);

      document.addEventListener('keydown', this.onKeyDown);
    }

    onSourcePointerDown(event) {
      if(event.pointerType !== 'touch') return;

      const slide = event.target.closest('.dual-media-slide');
      const media = slide?.querySelector('.dual-media-slide--media');
      const image = media?.querySelector('img');

      if(!slide || !media || !image) return;
      if(event.target.closest('a, button, video')) return;

      this.sourceTapStart = {
        image,
        x: event.clientX,
        y: event.clientY,
        time: performance.now()
      };
    }

    onSourcePointerUp(event) {
      if(event.pointerType !== 'touch' || !this.sourceTapStart) return;

      const start = this.sourceTapStart;
      this.sourceTapStart = null;

      const moved = Math.hypot(
        event.clientX - start.x,
        event.clientY - start.y
      );
      const elapsed = performance.now() - start.time;

      if(moved > 16 || elapsed > 420) {
        this.lastSourceTap = 0;
        return;
      }

      const now = performance.now();
      const isDoubleTap = now - this.lastSourceTap < 340;
      this.lastSourceTap = now;

      if(!isDoubleTap) return;

      event.preventDefault();
      event.stopPropagation();
      this.openZoom(start.image);
      this.lastSourceTap = 0;
    }

    openZoom(sourceImage) {
      if(!this.zoomModal || !this.zoomImage) return;

      this.zoomImage.src = sourceImage.currentSrc || sourceImage.src;
      this.zoomImage.alt = sourceImage.alt || '';

      this.zoomScale = 1;
      this.zoomX = 0;
      this.zoomY = 0;
      this.zoomPointers.clear();
      this.pointerStarts.clear();
      this.pinchStartDistance = 0;
      this.panStart = null;

      this.applyTransform();

      this.zoomModal.classList.remove('has-interacted');
      this.zoomModal.classList.add('is-open');
      this.zoomModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('rv-mobile-image-zoom-open');

      if(this.swiperMobile?.swiper) {
        this.swiperMobile.swiper.allowTouchMove = false;
        this.swiperMobile.swiper.autoplay?.stop?.();
      }

      setTimeout(() => {
        this.zoomModal.querySelector('.rv-mobile-image-zoom__close')?.focus({preventScroll: true});
      }, 0);
    }

    closeZoom() {
      if(!this.zoomModal) return;

      this.zoomModal.classList.remove('is-open', 'has-interacted');
      this.zoomModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('rv-mobile-image-zoom-open');

      this.zoomScale = 1;
      this.zoomX = 0;
      this.zoomY = 0;
      this.zoomPointers.clear();
      this.pointerStarts.clear();
      this.pinchStartDistance = 0;
      this.panStart = null;
      this.applyTransform();

      if(this.swiperMobile?.swiper) {
        this.swiperMobile.swiper.allowTouchMove = true;
        if(this.swiperMobile.swiper.params?.autoplay?.enabled) {
          this.swiperMobile.swiper.autoplay?.start?.();
        }
      }
    }

    onViewerPointerDown(event) {
      if(!this.zoomModal?.classList.contains('is-open')) return;

      event.preventDefault();
      this.zoomStage.setPointerCapture?.(event.pointerId);

      const point = {x: event.clientX, y: event.clientY};
      this.zoomPointers.set(event.pointerId, point);
      this.pointerStarts.set(event.pointerId, {...point, time: performance.now()});

      if(this.zoomPointers.size === 2) {
        const points = [...this.zoomPointers.values()];
        this.pinchStartDistance = Math.max(
          1,
          Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y)
        );
        this.pinchStartScale = this.zoomScale;
        this.panStart = null;
      } else if(this.zoomPointers.size === 1 && this.zoomScale > 1) {
        this.panStart = {
          pointerX: event.clientX,
          pointerY: event.clientY,
          x: this.zoomX,
          y: this.zoomY
        };
      }
    }

    onViewerPointerMove(event) {
      if(!this.zoomPointers.has(event.pointerId)) return;

      event.preventDefault();
      this.zoomPointers.set(event.pointerId, {x: event.clientX, y: event.clientY});

      if(this.zoomPointers.size >= 2) {
        const points = [...this.zoomPointers.values()].slice(0, 2);
        const distance = Math.hypot(
          points[1].x - points[0].x,
          points[1].y - points[0].y
        );

        this.zoomScale = this.clamp(
          this.pinchStartScale * (distance / this.pinchStartDistance),
          1,
          4
        );

        if(this.zoomScale <= 1.02) {
          this.zoomScale = 1;
          this.zoomX = 0;
          this.zoomY = 0;
        }

        this.clampPan();
        this.applyTransform();
        this.zoomModal?.classList.add('has-interacted');
        return;
      }

      if(this.zoomPointers.size === 1 && this.zoomScale > 1 && this.panStart) {
        this.zoomX = this.panStart.x + (event.clientX - this.panStart.pointerX);
        this.zoomY = this.panStart.y + (event.clientY - this.panStart.pointerY);

        this.clampPan();
        this.applyTransform();
        this.zoomModal?.classList.add('has-interacted');
      }
    }

    onViewerPointerUp(event) {
      if(!this.zoomPointers.has(event.pointerId)) return;

      const start = this.pointerStarts.get(event.pointerId);
      const end = this.zoomPointers.get(event.pointerId);

      this.zoomPointers.delete(event.pointerId);
      this.pointerStarts.delete(event.pointerId);
      this.zoomStage.releasePointerCapture?.(event.pointerId);

      if(
        event.pointerType === 'touch' &&
        start &&
        end &&
        this.zoomPointers.size === 0
      ) {
        const moved = Math.hypot(end.x - start.x, end.y - start.y);
        const elapsed = performance.now() - start.time;

        if(moved < 14 && elapsed < 360) {
          const now = performance.now();
          const isDoubleTap = now - this.lastViewerTap < 330;
          this.lastViewerTap = now;

          if(isDoubleTap) {
            this.toggleZoom();
            this.lastViewerTap = 0;
          }
        } else {
          this.lastViewerTap = 0;
        }
      }

      if(this.zoomPointers.size === 1 && this.zoomScale > 1) {
        const remaining = [...this.zoomPointers.values()][0];
        this.panStart = {
          pointerX: remaining.x,
          pointerY: remaining.y,
          x: this.zoomX,
          y: this.zoomY
        };
      } else {
        this.panStart = null;
      }
    }

    toggleZoom() {
      if(this.zoomScale > 1.15) {
        this.zoomScale = 1;
        this.zoomX = 0;
        this.zoomY = 0;
      } else {
        this.zoomScale = 2.5;
      }

      this.clampPan();
      this.applyTransform();
      this.zoomModal?.classList.add('has-interacted');
    }

    clampPan() {
      if(!this.zoomImage || this.zoomScale <= 1) {
        this.zoomX = 0;
        this.zoomY = 0;
        return;
      }

      const maxX = Math.max(
        0,
        (this.zoomImage.clientWidth * (this.zoomScale - 1)) / 2
      );
      const maxY = Math.max(
        0,
        (this.zoomImage.clientHeight * (this.zoomScale - 1)) / 2
      );

      this.zoomX = this.clamp(this.zoomX, -maxX, maxX);
      this.zoomY = this.clamp(this.zoomY, -maxY, maxY);
    }

    applyTransform() {
      if(!this.zoomImage) return;
      this.zoomImage.style.transform =
        `translate3d(${this.zoomX}px, ${this.zoomY}px, 0) scale(${this.zoomScale})`;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    onKeyDown(event) {
      if(event.key === 'Escape' && this.zoomModal?.classList.contains('is-open')) {
        this.closeZoom();
      }
    }

    disconnectedCallback() {
      if(this.swiperLeft?.swiper) this.swiperLeft.swiper.destroy();
      if(this.swiperRight?.swiper) this.swiperRight.swiper.destroy();
      if(this.swiperMobile?.swiper) this.swiperMobile.swiper.destroy();

      const mobile = this.querySelector('.dual-media-slideshow--mobile');
      mobile?.removeEventListener('pointerdown', this.onSourcePointerDown);
      mobile?.removeEventListener('pointerup', this.onSourcePointerUp);

      document.removeEventListener('keydown', this.onKeyDown);
      this.zoomModal?.remove();
      this.zoomModal = null;
    }
  }

  customElements.define('dual-media-slideshow', DualMediaSlideshow, { extends: 'div' });
}